/**
 * ThemeContext — light / dark / system mode for OVACS.
 *
 * Source of truth at runtime is this provider. The inline script in
 * index.html bootstraps the right class on <html> before React mounts
 * so there's no light-mode flash on first paint when the user prefers
 * dark — but once React is up, this context owns the value.
 *
 * Storage:   localStorage('ovacs.theme') = 'light' | 'dark' | 'system'
 *            absent → first-paint inline script picked one based on OS
 *
 * API:
 *   const { theme, resolvedTheme, setTheme, toggle } = useTheme()
 *
 *   theme         — user preference: 'light' | 'dark' | 'system'
 *   resolvedTheme — what's actually applied: 'light' | 'dark'
 *   setTheme      — explicit set
 *   toggle        — light ↔ dark (system → opposite of current OS pref)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'ovacs.theme'

const ThemeContext = createContext({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
  toggle: () => {},
})

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStoredTheme() {
  // Default to LIGHT unless the user has explicitly chosen otherwise.
  // OS preference is intentionally ignored here — owner spec was "light
  // default, user can switch via the profile dropdown."
  if (typeof window === 'undefined') return 'light'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'light'
}

function applyTheme(resolved) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme)
  const [systemTheme, setSystemTheme] = useState(getSystemTheme)

  // Listen for OS-level theme changes (relevant only when theme === 'system')
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light')
    if (mql.addEventListener) mql.addEventListener('change', handler)
    else mql.addListener(handler) // Safari < 14
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler)
      else mql.removeListener(handler)
    }
  }, [])

  const resolvedTheme = theme === 'system' ? systemTheme : theme

  // Apply class + persist whenever the resolution changes.
  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark' && next !== 'system') return
    setThemeState(next)
    try { window.localStorage.setItem(STORAGE_KEY, next) } catch { /* quota / private mode */ }
  }, [])

  // Cycle through light ↔ dark only (skipping system).
  // If the user is on system, jump to whatever is OPPOSITE of the system value
  // so the click visibly does something.
  const toggle = useCallback(() => {
    if (theme === 'system') {
      setTheme(systemTheme === 'dark' ? 'light' : 'dark')
    } else {
      setTheme(theme === 'dark' ? 'light' : 'dark')
    }
  }, [theme, systemTheme, setTheme])

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, toggle }),
    [theme, resolvedTheme, setTheme, toggle],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  return useContext(ThemeContext)
}

export default ThemeContext
